import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Archive } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useEmergencyMutations } from '../hooks';
import { SUSPENDED_CLOSURE_TYPES } from '../types';
import type { ClosureType } from '../types';

interface SuspendedCloseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: number | null;
  onSuccess?: () => void;
}

/**
 * Case 360° (Lot 2) Stage 5 — archive a SUSPENDED matter. The only exit from
 * SUSPENDED: choose an exit path (transfer / waiver / administrative) and give
 * a reason; the matter closes as CANCELLED.
 */
export function SuspendedCloseDialog({
  open,
  onOpenChange,
  matterId,
  onSuccess,
}: SuspendedCloseDialogProps) {
  const { t } = useTranslation();
  const { closeSuspendedMutation } = useEmergencyMutations();

  const [closureType, setClosureType] = React.useState<
    Exclude<ClosureType, 'NORMAL'> | ''
  >('');
  const [closureReason, setClosureReason] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setClosureType('');
      setClosureReason('');
    }
  }, [open]);

  const canSubmit =
    !!matterId &&
    !!closureType &&
    !!closureReason.trim() &&
    !closeSuspendedMutation.isPending;

  const handleSubmit = async () => {
    if (!matterId || !closureType || !canSubmit) return;
    await closeSuspendedMutation.mutateAsync({
      id: matterId,
      data: { closureType, closureReason: closureReason.trim() },
    });
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-4 w-4 text-slate-500" />
            {t('emergency.archiveDialogTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('emergency.archiveDialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 space-y-4">
          {/* Exit path */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('emergency.closureTypeLabel')}
            </label>
            <Select
              value={closureType}
              onValueChange={(v) =>
                setClosureType(v as Exclude<ClosureType, 'NORMAL'>)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('emergency.selectClosureType')} />
              </SelectTrigger>
              <SelectContent>
                {SUSPENDED_CLOSURE_TYPES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {t(`emergency.closureType.${c}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason (always required) */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('emergency.closureReasonLabel')}
            </label>
            <Textarea
              value={closureReason}
              onChange={(e) => setClosureReason(e.target.value)}
              placeholder={t('emergency.closureReasonPlaceholder')}
              rows={3}
              maxLength={1000}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={closeSuspendedMutation.isPending}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {closeSuspendedMutation.isPending
              ? t('processing')
              : t('emergency.archiveConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
